import { useContext } from 'react';
import AppContext from '../../../AppContext';
import QtrBookOfBusinessRowData from './QtrBookOfBusinessRowData';
import styles from './QtrBookOfBusinessRow.module.css';
import { RatioFormat, DollarFormat } from '../../format';
import RowHelmet from '../RowHelmet';
import LabelRow from '../LabelRow';
import BlankRow from '../BlankRow/BlankRow';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

interface QtrBookOfBusinessRowProps extends QtrBookOfBusinessRowData {
  /** Whether to include Loss Ratio columns (LOB layout vs States layout) */
  readonly includeLossRatio?: boolean;
}

function rowClassSwitch(rowClass: string = '') {
  switch (rowClass) {
    case 'INDENT':
      return 'indent';
    case 'TOTAL':
      return 'total';
    case 'GRAND_TOTAL':
      return 'grand_total';
    case 'BOLD':
      return 'bold';
    default:
      return '';
  }
}

function hasAnyOutsideBenchmarks(data: QtrBookOfBusinessRowData) {
  return !!(
    data.premiumPercentChange?.outsideBenchmark ||
    data.cytdPremium?.outsideBenchmark ||
    data.cytdPercentOfTotal?.outsideBenchmark ||
    data.cytdLosses?.outsideBenchmark ||
    data.cytdLossRatio?.outsideBenchmark ||
    data.pytdPremium?.outsideBenchmark ||
    data.pytdPercentOfTotal?.outsideBenchmark ||
    data.pytdLosses?.outsideBenchmark ||
    data.pytdLossRatio?.outsideBenchmark
  );
}

function getDataDisplayForCurrentRepository(
  riskRepository: string | null,
  rowDisplay: QtrBookOfBusinessRowData['rowDisplay']
) {
  if (riskRepository === 'FR' || !rowDisplay || !riskRepository) {
    return '';
  }
  const displayValue = rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay];
  // Match the standard BookOfBusinessRow convention: a repository may GREY out a row,
  // but HIDE must not remove it from view. The rows still belong to the section that is
  // being displayed, so hiding them all would leave the section empty.
  if (displayValue === 'GREY') return 'grey';
  return '';
}

export default function QtrBookOfBusinessRow({
  sectionKey,
  subSectionKey,
  rowKey,
  rowType,
  rowClass,
  label,
  benchmark,
  rowDisplay,
  calculateDetail,
  rowDataSource,
  rowLinkConfiguration,
  premiumPercentChange,
  cytdPremium,
  cytdPercentOfTotal,
  cytdLosses,
  cytdLossRatio,
  pytdPremium,
  pytdPercentOfTotal,
  pytdLosses,
  pytdLossRatio,
  includeLossRatio = false
}: Readonly<QtrBookOfBusinessRowProps>) {
  const { showBenchmark, riskRepository } = useContext(AppContext);

  if (label.trim().toLowerCase() === 'missing') {
    return null;
  }

  if (rowType === 'BLANK') {
    return <BlankRow />;
  }

  const repoDisplay = getDataDisplayForCurrentRepository(riskRepository, rowDisplay);

  const className = cx(
    'container',
    { collapse: !showBenchmark },
    repoDisplay,
    rowClassSwitch(rowClass)
  );

  if (rowType === 'LABEL') {
    const id =
      'label-' +
      label
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]+/g, '')
        .toLowerCase();
    return (
      <div id={id} data-testid={id} className={className}>
        <RowHelmet label={label} rowLinkConfiguration={rowLinkConfiguration} />
        <LabelRow />
      </div>
    );
  }

  const data: QtrBookOfBusinessRowData = {
    rowKey,
    rowType,
    rowClass,
    label,
    benchmark,
    rowDisplay,
    calculateDetail,
    rowDataSource,
    rowLinkConfiguration,
    premiumPercentChange,
    cytdPremium,
    cytdPercentOfTotal,
    cytdLosses,
    cytdLossRatio,
    pytdPremium,
    pytdPercentOfTotal,
    pytdLosses,
    pytdLossRatio
  };

  const id = sectionKey ? `${sectionKey}-${rowKey}` : `rowKey-${rowKey}`;

  return (
    <div id={id} data-testid="qtr-book-of-business-data-row" className={className}>
      <RowHelmet
        label={label}
        rowKey={rowKey}
        isOutsideBenchmark={hasAnyOutsideBenchmarks(data)}
        benchmarkText={benchmark}
        calculateDetail={calculateDetail}
        rowType={rowType}
        rowDataSource={rowDataSource}
        rowLinkConfiguration={rowLinkConfiguration}
        sectionKey={sectionKey}
        subSectionKey={subSectionKey}
      />

      {/* 5: Premium % Change */}
      <RatioFormat {...premiumPercentChange} showDefaultValue={true} />

      {/* 6: CYTD Premium */}
      <DollarFormat {...cytdPremium} />

      {/* 7: CYTD % of Total */}
      <RatioFormat {...cytdPercentOfTotal} />

      {/* 8: CYTD Losses */}
      <DollarFormat {...cytdLosses} />

      {/* 9: CYTD Loss Ratio (always rendered for grid; value only for LOB) */}
      {includeLossRatio ? <RatioFormat {...cytdLossRatio} /> : <p></p>}

      {/* 10: PYTD Premium */}
      <DollarFormat {...pytdPremium} />

      {/* 11: PYTD % of Total */}
      <RatioFormat {...pytdPercentOfTotal} />

      {/* 12: PYTD Losses */}
      <DollarFormat {...pytdLosses} />

      {/* 13: PYTD Loss Ratio (always rendered for grid; value only for LOB) */}
      {includeLossRatio ? <RatioFormat {...pytdLossRatio} /> : <p></p>}
    </div>
  );
}
